import { AnalyticsSdk, generateUUID, getTimeZone, getSessionID, fetchLocusData, buildSearchContext } from '../pdtUtils';
import {
  LOB_RAILS,
  EVENT_NAME_PAGE_RENDERED,
  EVENT_TYPE_PAGE,
  STORE_ID_MMT,
  ORGANISATION_MMT,
  BU_GROUND_TRANSPORT,
  TRAFFIC_SOURCE_ORGANIC,
  PDT_SCHEMA_ID,
  PDT_SCHEMA_NAME,
  PAGE_TYPE_TEMPLATE,
  PAGE_NAME_SEO,
  FUNNEL_STEP_DETAIL,
  FUNNEL_SOURCE_SEO,
  SEO_PAGE_EVENT,
  XDM_PAGE_NAME
} from '../constants';

interface TrainData {
  trainNumber?: string;
  trainName?: string;
  fromStnCode?: string;
  fromStnName?: string;
  toStnCode?: string;
  toStnName?: string;
  departureTime?: string;
  departureDate?: string;
  arrivalTime?: string;
  arrivalDate?: string;
  duration?: string;
  avlClasses?: string[];
  runningSun?: string;
  runningMon?: string;
  runningTue?: string;
  runningWed?: string;
  runningThu?: string;
  runningFri?: string;
  runningSat?: string;
}

interface TbcPageData {
  fromCity?: string;
  fromCityCode?: string;
  toCity?: string;
  toCityCode?: string;
  trainsMap?: Record<string, TrainData>;
  weeklyFestiveTrainList?: { list?: TrainData[] };
  weeklyCovidTrainList?: { list?: TrainData[] };
}

const DAYS_MAP: { key: keyof TrainData; day: string }[] = [
  { key: 'runningSun', day: 'Sun' },
  { key: 'runningMon', day: 'Mon' },
  { key: 'runningTue', day: 'Tue' },
  { key: 'runningWed', day: 'Wed' },
  { key: 'runningThu', day: 'Thu' },
  { key: 'runningFri', day: 'Fri' },
  { key: 'runningSat', day: 'Sat' }
];

function parseDurationToMinutes(duration?: string): number | null {
  if (!duration) return null;
  const hourMatch = duration.match(/(\d+)\s*hr/i);
  const minMatch = duration.match(/(\d+)\s*min/i);
  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const mins = minMatch ? parseInt(minMatch[1], 10) : 0;
  return hours * 60 + mins;
}

function buildProductList(trains: TrainData[]) {
  return (
    trains?.map((train, index) => ({
      id: train?.trainNumber || 'NA',
      train_number: parseInt(train?.trainNumber || '0', 10) || 0,
      train_name: train?.trainName || 'NA',
      duration: parseDurationToMinutes(train?.duration),
      position: { v: index + 1 },
      from_station: {
        code: train?.fromStnCode || 'NA',
        name: train?.fromStnName || 'NA',
        departure_time: train?.departureTime || null,
        departure_date: train?.departureDate || null
      },
      to_station: {
        code: train?.toStnCode || 'NA',
        name: train?.toStnName || 'NA',
        arrival_time: train?.arrivalTime || null,
        arrival_date: train?.arrivalDate || null
      },
      product_type: 'train',
      days_of_run: DAYS_MAP.map(({ key, day }) => ({ day, running: train?.[key] === 'Y' })),
      class: train?.avlClasses?.join(',') || null
    })) || []
  );
}

export const logTbcPageLoadEvent = async (tbcData?: TbcPageData) => {
  try {
    if (typeof window === 'undefined' || !AnalyticsSdk || !tbcData) return;

    const isDev = process.env.NODE_ENV !== 'production';
    const sessionId = getSessionID();
    const journeyId = generateUUID();
    const locusData = await fetchLocusData(tbcData?.fromCityCode, tbcData?.toCityCode);

    const searchContext = buildSearchContext({
      fromCode: tbcData?.fromCityCode,
      fromName: tbcData?.fromCity,
      toCode: tbcData?.toCityCode,
      toName: tbcData?.toCity,
      locusData
    });

    const allTrains = [
      ...Object.values(tbcData?.trainsMap || {}),
      ...(tbcData?.weeklyFestiveTrainList?.list || []),
      ...(tbcData?.weeklyCovidTrainList?.list || [])
    ];

    const components = {
      id: 'tbc_page_load',
      component_type: 'route_listing',
      product_list: buildProductList(allTrains),
      content_details: [
        {
          id: 'tbc_route',
          type: 'route',
          position: { v: 1 },
          from: {
            location: {
              code: tbcData?.fromCityCode || 'NA',
              name: tbcData?.fromCity || 'NA',
              country_code: 'IN',
              country: 'India'
            },
            locus: {
              locus_id: locusData?.fromLocusCode || 'NA',
              locus_type: locusData?.sourceLocusType || 'NA',
              country: 'India',
              locus_v2_id: locusData?.sourceLocusV2Id || 'NA',
              locus_v2_type: locusData?.sourceLocusV2Type || 'NA'
            }
          },
          to: {
            location: {
              code: tbcData?.toCityCode || 'NA',
              name: tbcData?.toCity || 'NA',
              country_code: 'IN',
              country: 'India'
            },
            locus: {
              locus_id: locusData?.toLocusCode || 'NA',
              locus_type: locusData?.destinationLocusType || 'NA',
              country: 'India',
              locus_v2_id: locusData?.destinationLocusV2Id || 'NA',
              locus_v2_type: locusData?.destinationLocusV2Type || 'NA'
            }
          }
        }
      ]
    };

    const payload = {
      page_context: {
        lob: LOB_RAILS,
        page_type: PAGE_TYPE_TEMPLATE,
        page_name: XDM_PAGE_NAME.ROUTE,
        prev_page_name: PAGE_NAME_SEO,
        funnel_step: FUNNEL_STEP_DETAIL,
        lob_category: LOB_RAILS,
        travel_store: { store_id: STORE_ID_MMT }
      },
      event_tracking_context: {
        env: isDev ? 'dev' : 'prod',
        timezone: getTimeZone(),
        session_id: sessionId,
        funnel_source: FUNNEL_SOURCE_SEO,
        bu: BU_GROUND_TRANSPORT,
        organisation: ORGANISATION_MMT,
        journey_id: journeyId,
        traffic_source: TRAFFIC_SOURCE_ORGANIC,
        template_id: PDT_SCHEMA_ID,
        topic_name: PDT_SCHEMA_NAME
      },
      search_context: searchContext,
      event_detail: {
        event_name: EVENT_NAME_PAGE_RENDERED,
        event_type: EVENT_TYPE_PAGE,
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_value: SEO_PAGE_EVENT.ROUTE,
        components
      },
      experiment_details: {
        valid_exp_list: [] as string[],
        variant_keys: '',
        honoured_exp_list: [] as string[]
      }
    };

    const xdmPayload = {
      ...payload,
      event_detail: {
        ...payload.event_detail,
        components: [components]
      }
    };

    const extraOptions = { lob: LOB_RAILS, destinationUrl: window?.location?.href || '' };

    AnalyticsSdk?.pushToPDT?.(payload, extraOptions);
    AnalyticsSdk?.pushToAdobeDataLayer?.(xdmPayload, extraOptions);
  } catch (e) {
    console.error('Analytics: route page load event failed', e);
  }
};
